const transcriptomeResequencing = {
  title: "Transcriptome Resequencing Documentation",

  fields: [
    {
      title: "Project Name",

      description:
        "Provide a meaningful and unique name for your Transcriptome Resequencing project. This name will be used to identify the analysis throughout the platform.",

      examples: [
        "Breast_Cancer_RNASeq",
        "Drug_Response_Study",
        "Liver_Transcriptome",
      ],

      notes: [
        "Choose a descriptive project name.",
        "Avoid unnecessary special characters.",
        "The project name will appear in your dashboard and reports.",
      ],
    },

    {
      title: "Number of Samples",

      description:
        "Specify the total number of RNA sequencing samples included in the analysis. Credits are calculated automatically based on this value.",

      examples: [
        "2",
        "6",
        "24",
      ],

      notes: [
        "Must be greater than zero.",
        "Ensure the number matches the samples entered in the Metadata Builder.",
      ],
    },

    {
      title: "Short Notes",

      description:
        "Provide any additional information or special instructions that may assist the bioinformatics team during analysis.",

      examples: [
        "Tumor vs Normal comparison.",
        "Poly-A enriched RNA sequencing.",
        "Paired-end Illumina sequencing.",
      ],

      notes: [
        "Optional field.",
        "Include any experimental details that may help with the analysis.",
      ],
    },

    {
      title: "Raw Data Source",

      description:
        "Specify how your sequencing data will be provided for analysis.",

      examples: [
        "Online",
        "Offline",
      ],

      notes: [
        "For Online, provide the download link to your sequencing files.",
        "Username and password may be supplied if the data repository requires authentication.",
        "If Offline is selected, our team will contact you regarding the data transfer process.",
      ],
    },
    {
      title: "Organism",

      description:
        "Search and select the organism corresponding to your RNA sequencing experiment. The selected organism determines the reference genome and annotation files used throughout the analysis.",

      notes: [
        "Start typing at least three characters to search.",
        "Select the organism that matches your sequencing experiment.",
        "Choosing the correct organism ensures accurate read alignment and downstream analysis.",
      ],
    },

    {
      title: "Alignment Tool",

      description:
        "Select the aligner used for mapping RNA-Seq reads to the reference genome. Transcriptome Resequencing currently uses the STAR aligner for fast and splice-aware alignment.",

      examples: [
        "STAR",
      ],

      notes: [
        "STAR is optimized for RNA sequencing data.",
        "It accurately aligns reads spanning exon-exon junctions.",
      ],
    },

    {
      title: "Metadata Builder",

      description:
        "Enter metadata for every sequencing sample included in the study. Each sample requires a unique Sample ID and an experimental Condition.",

      examples: [
        "Sample_001  |  CONTROL",
        "Sample_002  |  TREATED",
        "Sample_003  |  KNOCKOUT",
      ],

      notes: [
        "Each Sample ID must be unique.",
        "Condition names are automatically converted to uppercase.",
        "Samples having the same Condition are treated as biological replicates.",
        "Click 'Add Sample' to include additional samples.",
        "Click 'Save Metadata' after entering all samples.",
        "Use 'Modify Metadata' to edit the metadata later.",
      ],
    },

    {
      title: "Comparison Builder",

      description:
        "Define the experimental comparisons that will be used for Differential Gene Expression analysis. Comparisons can either be generated automatically or selected manually.",

      sections: [
        {
          title: "Automatic Comparisons",

          description:
            "Automatically generates every possible Test vs Control comparison from the unique experimental conditions entered in the Metadata Builder.",

          notes: [
            "Requires at least two unique conditions.",
            "Generates ordered comparisons (e.g., CONTROL vs TREATED and TREATED vs CONTROL).",
            "Useful for exploratory analysis involving multiple experimental groups.",
          ],
        },

        {
          title: "Manual Comparisons",

          description:
            "Allows you to manually select Test and Control conditions for Differential Expression analysis.",

          notes: [
            "Select Test and Control from the available conditions.",
            "Duplicate comparisons are not allowed.",
            "Click 'Add' to include a comparison.",
            "Click 'Save Comparisons' after finalizing the list.",
            "Use 'Remove' to delete unwanted comparisons before saving.",
          ],
        },
      ],
    },

    {
      title: "Raw Data QC Parameters",

      description:
        "Configure quality control settings applied to sequencing reads before downstream expression analysis.",

      sections: [
        {
          title: "Phred Quality Score",

          description:
            "Defines the minimum acceptable sequencing quality score used during quality filtering.",

          notes: [
            "Allowed range: 10–40.",
            "Higher values apply stricter filtering.",
            "Reads with lower quality bases may be trimmed or removed.",
          ],
        },

        {
          title: "Minimum Read Length Required",

          description:
            "Specifies the minimum read length that must remain after trimming for a read to be retained.",

          notes: [
            "Allowed range: 50–120 bases.",
            "Reads shorter than this threshold will be discarded.",
          ],
        },
      ],
    },

    {
      title: "Thresholds for Differential Gene Expression",

      description:
        "Configure the statistical criteria used to identify significantly differentially expressed genes.",

      sections: [
        {
          title: "Log2 Fold Change",

          description:
            "Defines the minimum expression change required for a gene to be reported as differentially expressed.",

          notes: [
            "Allowed range: 0.25–5.",
            "Lower values detect subtle expression changes.",
            "Higher values report only strongly regulated genes.",
          ],
        },

        {
          title: "Significance Threshold",

          description:
            "Choose the statistical metric used to determine differential expression significance.",

          notes: [
            "Select either p-value or q-value (False Discovery Rate).",
            "Threshold value must be between 0 and 1.",
            "Lower values indicate stricter statistical significance.",
          ],
        },
      ],
    },

    {
      title: "Annotation Parameters",

      description:
        "Configure statistical thresholds used during downstream functional enrichment analysis such as Gene Ontology (GO) and pathway enrichment.",

      sections: [
        {
          title: "Significance Threshold for Enrichment",

          description:
            "Specify the statistical cutoff for reporting significantly enriched biological functions and pathways.",

          notes: [
            "Select either p-value or q-value (False Discovery Rate).",
            "Threshold value must be between 0 and 1.",
            "Smaller values provide more stringent enrichment results.",
          ],
        },
      ],
    },
  ],
};

export default transcriptomeResequencing;